import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Plus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { graphql } from '@/gql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { SortableList } from '@/components/ui/SortableList';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { eurosToCents, formatCurrency } from '@/lib/format';
import { useBackHref } from '@/hooks/useListLocation';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { doorsDefaultsProblem } from './doorsWindow';
import { EventFormRow, type EventFormValues } from './EventFormRow';
import { useStageChannels } from './useStageChannels';

const BlueprintDoc = graphql(/* GraphQL */ `
  query Blueprint($id: UUID!) {
    blueprint(id: $id) {
      id
      title
      requireTicket
      minimumAge
      doorsOpen
      doorsClose
      ticketText
      dresscode
      isActive
      nightCount
      events {
        id
        title
        startTime
        endTime
        description
        posterUrl
        entrancePriceLabel
        offersTickets
      }
      stages {
        id
        name
        sort
        ticketPrice
        feePrice
        maxAmount
        websiteSale
        appSale
        posSale
        linkSale
        door
        memberSale
        isActive
        entryFrom
        entryUntil
        lateFee
      }
    }
  }
`);

const CreateBlueprintDoc = graphql(/* GraphQL */ `
  mutation CreateBlueprint($input: CreateBlueprintInput!) {
    createBlueprint(input: $input) {
      id
    }
  }
`);

const UpdateBlueprintDoc = graphql(/* GraphQL */ `
  mutation UpdateBlueprint($id: UUID!, $input: UpdateBlueprintInput!) {
    updateBlueprint(id: $id, input: $input) {
      id
      isActive
    }
  }
`);

const CreateBlueprintStageDoc = graphql(/* GraphQL */ `
  mutation CreateBlueprintStage($blueprintId: UUID!, $input: BlueprintStageInput!) {
    createBlueprintStage(blueprintId: $blueprintId, input: $input) {
      id
    }
  }
`);

const UpdateBlueprintStageDoc = graphql(/* GraphQL */ `
  mutation UpdateBlueprintStage($id: UUID!, $input: BlueprintStageInput!) {
    updateBlueprintStage(id: $id, input: $input) {
      id
      isActive
    }
  }
`);

const ReorderBlueprintStagesDoc = graphql(/* GraphQL */ `
  mutation ReorderBlueprintStages($blueprintId: UUID!, $orderedIds: [UUID!]!) {
    reorderBlueprintStages(blueprintId: $blueprintId, orderedIds: $orderedIds) {
      id
      sort
    }
  }
`);

const CreateBlueprintEventDoc = graphql(/* GraphQL */ `
  mutation CreateBlueprintEvent($blueprintId: UUID!, $input: BlueprintEventInput!) {
    createBlueprintEvent(blueprintId: $blueprintId, input: $input) {
      id
    }
  }
`);

const UpdateBlueprintEventDoc = graphql(/* GraphQL */ `
  mutation UpdateBlueprintEvent($id: UUID!, $input: BlueprintEventInput!) {
    updateBlueprintEvent(id: $id, input: $input) {
      id
    }
  }
`);

const DeleteBlueprintEventDoc = graphql(/* GraphQL */ `
  mutation DeleteBlueprintEvent($id: UUID!) {
    deleteBlueprintEvent(id: $id)
  }
`);

/** The event form's values as Go's BlueprintEventInput (times of day stay `HH:MM`; an empty end clears). */
function blueprintEventInput(v: EventFormValues) {
  return {
    title: v.title,
    startTime: v.start,
    endTime: v.end || null,
    description: v.description,
    posterPath: v.posterPath,
    entrancePriceLabel: v.entrancePriceLabel,
    offersTickets: v.offersTickets,
  };
}

export function BlueprintEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const backHref = useBackHref('/blueprints');
  const isNew = !id || id === 'new';

  const { data, loading, error, refetch } = useQuery(BlueprintDoc, {
    variables: { id: id ?? '' },
    skip: isNew,
    errorPolicy: 'all',
  });
  const blueprint = data?.blueprint;

  const [title, setTitle] = useState('');
  const [requireTicket, setRequireTicket] = useState(false);
  const [minimumAge, setMinimumAge] = useState('');
  const [doorsOpen, setDoorsOpen] = useState('');
  const [doorsClose, setDoorsClose] = useState('');
  const [ticketText, setTicketText] = useState('');
  const [dresscode, setDresscode] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(null);
  // Unsaved default-event forms (Add event), listed after the saved ones; the number is only a React key.
  const [drafts, setDrafts] = useState<number[]>([]);

  if (blueprint && blueprint.id !== seededFor) {
    setSeededFor(blueprint.id);
    setTitle(blueprint.title);
    setRequireTicket(blueprint.requireTicket);
    setMinimumAge(blueprint.minimumAge != null ? String(blueprint.minimumAge) : '');
    setDoorsOpen(blueprint.doorsOpen);
    setDoorsClose(blueprint.doorsClose);
    setTicketText(blueprint.ticketText ?? '');
    setDresscode(blueprint.dresscode ?? '');
    setIsActive(blueprint.isActive);
  }

  const doorsProblem = doorsDefaultsProblem(doorsOpen, doorsClose);

  const [createBlueprint, { loading: creating }] = useMutation(CreateBlueprintDoc);
  const [updateBlueprint, { loading: updating }] = useMutation(UpdateBlueprintDoc);
  const [createBlueprintEvent] = useMutation(CreateBlueprintEventDoc);
  const [updateBlueprintEvent] = useMutation(UpdateBlueprintEventDoc);
  const [deleteBlueprintEvent] = useMutation(DeleteBlueprintEventDoc);
  const [createStage] = useMutation(CreateBlueprintStageDoc);
  const [updateStage] = useMutation(UpdateBlueprintStageDoc);
  const [reorderStages] = useMutation(ReorderBlueprintStagesDoc);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (doorsProblem) return;
    setFormError(null);
    const input = {
      title: title.trim(),
      requireTicket,
      minimumAge: minimumAge ? Number.parseInt(minimumAge, 10) : null,
      doorsOpen,
      doorsClose,
      // Trimmed; an empty string clears it (null would leave an existing text alone on update).
      ticketText: ticketText.trim(),
      dresscode: dresscode.trim(),
      isActive,
    };
    try {
      if (isNew) {
        const result = await createBlueprint({ variables: { input } });
        const newId = result.data?.createBlueprint.id;
        if (newId) navigate(`/blueprints/${newId}`, { replace: true, state: { from: backHref } });
      } else if (id) {
        await updateBlueprint({ variables: { id, input } });
        await refetch();
      }
    } catch (err) {
      setFormError(graphqlErrorMessage(err));
    }
  };

  if (!isNew && loading && !blueprint && !error) {
    return <p className="text-sm text-gray-500">Loading blueprint…</p>;
  }
  if (!isNew && error && !blueprint) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Blueprint
        </h1>
        <QueryErrorNotice error={error} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FormLayout
        title={isNew ? 'New blueprint' : blueprint?.title ?? 'Blueprint'}
        description="A blueprint with no date. Default doors are a time of day (Europe/Amsterdam), not a datetime. Default stages have no sale window — those only exist on a dated night."
        onSubmit={handleSubmit}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-3">
            {formError ? <span className="text-sm text-red-600">{formError}</span> : null}
            <Button variant="ghost" type="button" onClick={() => navigate(backHref)}>
              Cancel
            </Button>
            <Button type="submit" loading={creating || updating} disabled={doorsProblem !== null}>
              {isNew ? 'Create blueprint' : 'Save blueprint'}
            </Button>
          </div>
        }
      >
        <FormSection title="Blueprint">
          <FormField label="Title" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </FormField>
          <Switch
            label="Require ticket"
            description="Creating a ticketed blueprint seeds a Regular stage (website + app both on)."
            checked={requireTicket}
            onChange={(e) => setRequireTicket(e.target.checked)}
          />
          <FormField label="Minimum age">
            <Input
              type="number"
              min={0}
              value={minimumAge}
              onChange={(e) => setMinimumAge(e.target.value)}
            />
          </FormField>
          <FormField
            label="Ticket text"
            description="Copied to each night created from this blueprint. Shown to the holder of a claimed ticket on the ticket e-mail, PDF, ticket pages and app. Never on the website."
          >
            <textarea
              id="admin.blueprints.ticketText"
              value={ticketText}
              maxLength={1000}
              onChange={(e) => setTicketText(e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            />
          </FormField>
          <FormField
            label="Dress code"
            description="Copied to each night created from this blueprint. Shown on the website's Good to know and in the app; empty shows 'None, come as you are'."
          >
            <Input
              id="admin.blueprints.dresscode"
              value={dresscode}
              maxLength={100}
              onChange={(e) => setDresscode(e.target.value)}
            />
          </FormField>
          <FormField
            label="Active"
            description="When off, this blueprint is hidden from the night picker. Existing nights are left alone."
          >
            <Switch
              id="admin.blueprints.isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              label={isActive ? 'Active' : 'Inactive'}
            />
          </FormField>
        </FormSection>
        <FormSection
          title="Default doors"
          description="Required. Time of day only (HH:MM). If close is ≤ open, close is the next calendar day when snapshotting a night."
        >
          <FormField label="Doors open" required error={doorsOpen ? undefined : doorsProblem}>
            <Input
              id="admin.blueprints.doorsOpen"
              type="time"
              value={doorsOpen}
              onChange={(e) => setDoorsOpen(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Doors close" required error={doorsOpen ? doorsProblem : undefined}>
            <Input
              id="admin.blueprints.doorsClose"
              type="time"
              value={doorsClose}
              onChange={(e) => setDoorsClose(e.target.value)}
              required
            />
          </FormField>
        </FormSection>
      </FormLayout>

      {!isNew && blueprint ? (
        <Card
          title="Default events"
          subtitle="What the public sees on each night created from this blueprint: title, time of day, description, poster and entrance price. Times must lie inside the default doors; after-midnight times land on the next day. At most one event offers tickets. Nights that already exist keep their own events."
          actions={
            <Button
              type="button"
              size="sm"
              id="admin.blueprints.events.add"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setDrafts((d) => [...d, (d[d.length - 1] ?? 0) + 1])}
            >
              Add event
            </Button>
          }
        >
          <div className="space-y-3">
            {blueprint.events.map((event, i) => (
              <EventFormRow
                ticketsAllowed={requireTicket}
                ticketsNoun="blueprint"
                key={event.id}
                idPrefix="admin.blueprints.event"
                index={i}
                timeInput="time"
                deleteNoun="default event"
                event={{ ...event, start: event.startTime, end: event.endTime ?? '' }}
                defaultStart={blueprint.doorsOpen}
                onSave={async (values) => {
                  await updateBlueprintEvent({ variables: { id: event.id, input: blueprintEventInput(values) } });
                  await refetch();
                }}
                onDelete={async () => {
                  await deleteBlueprintEvent({ variables: { id: event.id } });
                  await refetch();
                }}
              />
            ))}
            {drafts.map((key, j) => (
              <EventFormRow
                ticketsAllowed={requireTicket}
                ticketsNoun="blueprint"
                key={`draft-${key}`}
                idPrefix="admin.blueprints.event"
                index={blueprint.events.length + j}
                timeInput="time"
                deleteNoun="default event"
                defaultStart={blueprint.doorsOpen}
                onSave={async (values) => {
                  await createBlueprintEvent({ variables: { blueprintId: blueprint.id, input: blueprintEventInput(values) } });
                  await refetch();
                  setDrafts((d) => d.filter((k) => k !== key));
                }}
                onDelete={async () => setDrafts((d) => d.filter((k) => k !== key))}
              />
            ))}
            {blueprint.events.length + drafts.length === 0 ? (
              <p className="text-sm text-gray-500">
                No default events yet. A night created from this blueprint starts without events.
              </p>
            ) : null}
          </div>
        </Card>
      ) : null}

      {!isNew && blueprint && requireTicket ? (
        <Card
          title="Default stages"
            subtitle="No sale window on a blueprint. Link is a stage sold only through its link and cannot be combined; Website requires App. Member is not a free-form checkbox — it is created on a night. An inactive stage is not copied onto new nights; existing night stages stay."
          actions={
            <Button
              type="button"
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={async () => {
                if (!id) return;
                try {
                  await createStage({
                    variables: {
                      blueprintId: id,
                      input: {
                        name: 'New stage',
                        ticketPriceCents: 0,
                        feePriceCents: 0,
                        websiteSale: true,
                        appSale: true,
                        posSale: false,
                        door: false,
                        linkSale: false,
                        isActive: true,
                        entryFrom: null,
                        entryUntil: null,
                        lateFeeCents: null,
                      },
                    },
                  });
                  await refetch();
                } catch (err) {
                  setFormError(graphqlErrorMessage(err));
                }
              }}
            >
              Add stage
            </Button>
          }
        >
          <SortableList
            items={blueprint.stages}
            onReorder={async (orderedIds) => {
              if (!id) return;
              await reorderStages({ variables: { blueprintId: id, orderedIds } });
              await refetch();
            }}
            renderItem={(stage, { dragHandle }) => (
              <BlueprintStageRow
                key={stage.id}
                stage={stage}
                index={blueprint.stages.findIndex((row) => row.id === stage.id)}
                onSave={async (input) => {
                  await updateStage({ variables: { id: stage.id, input } });
                  await refetch();
                }}
                dragHandle={dragHandle}
              />
            )}
          />
        </Card>
      ) : null}
    </div>
  );
}

/** E2E id of one entry-window control in the `index`-th blueprint stage row (0-based, row order). */
const blueprintStageControlId = (index: number, field: 'entryFrom' | 'entryUntil' | 'lateFee') =>
  `admin.blueprints.stage.${index}.${field}`;

function BlueprintStageRow({
  stage,
  index,
  onSave,
  dragHandle,
}: {
  index: number;
  stage: {
    id: string;
    name: string;
    ticketPrice: number;
    feePrice: number;
    maxAmount?: number | null;
    websiteSale: boolean;
    appSale: boolean;
    posSale: boolean;
    door: boolean;
    linkSale: boolean;
    memberSale: boolean;
    isActive: boolean;
    entryFrom?: string | null;
    entryUntil?: string | null;
    lateFee?: number | null;
  };
  onSave: (input: {
    name: string;
    ticketPriceCents: number;
    feePriceCents: number;
    maxAmount?: number | null;
    websiteSale: boolean;
    appSale: boolean;
    posSale: boolean;
    door: boolean;
    linkSale: boolean;
    isActive: boolean;
    entryFrom: string | null;
    entryUntil: string | null;
    lateFeeCents: number | null;
  }) => Promise<void>;
  dragHandle: React.ReactNode;
}) {
  const [name, setName] = useState(stage.name);
  const [price, setPrice] = useState(String(stage.ticketPrice));
  const [fee, setFee] = useState(String(stage.feePrice));
  const [maxAmount, setMaxAmount] = useState(
    stage.maxAmount != null ? String(stage.maxAmount) : '',
  );
  const [entryFrom, setEntryFrom] = useState(stage.entryFrom ?? '');
  const [entryUntil, setEntryUntil] = useState(stage.entryUntil ?? '');
  const [lateFee, setLateFee] = useState(stage.lateFee != null ? String(stage.lateFee) : '');
  const {
    channels: { websiteSale, appSale, posSale, door, linkSale },
    setLink,
    setWebsite,
    setApp,
    setPos,
    setDoor,
  } = useStageChannels(stage);
  const [isActive, setIsActive] = useState(stage.isActive);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 p-3 dark:border-white/10">
      <div className="flex items-center gap-2">
        {dragHandle}
        <Input value={name} onChange={(e) => setName(e.target.value)} className="flex-1" />
        {stage.memberSale ? (
          <span className="text-xs text-gray-500">Member (night only)</span>
        ) : null}
        {!isActive ? <Badge tone="neutral">Inactive</Badge> : null}
        <Switch
          id="admin.blueprints.stageActive"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          label={isActive ? 'Active' : 'Inactive'}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <FormField label="Admission (€)">
          <Input type="number" step="0.01" min={0} value={price} onChange={(e) => setPrice(e.target.value)} />
        </FormField>
        <FormField label="Booking fee (€)">
          <Input type="number" step="0.01" min={0} value={fee} onChange={(e) => setFee(e.target.value)} />
        </FormField>
        <FormField label="Max sold">
          <Input type="number" min={0} value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
        </FormField>
        <FormField
          label="Entry from"
          htmlFor={blueprintStageControlId(index, 'entryFrom')}
          description="Empty = doors open."
        >
          <Input
            id={blueprintStageControlId(index, 'entryFrom')}
            type="time"
            value={entryFrom}
            onChange={(e) => setEntryFrom(e.target.value)}
          />
        </FormField>
        <FormField
          label="Entry until"
          htmlFor={blueprintStageControlId(index, 'entryUntil')}
          description="Empty = doors close."
        >
          <Input
            id={blueprintStageControlId(index, 'entryUntil')}
            type="time"
            value={entryUntil}
            onChange={(e) => setEntryUntil(e.target.value)}
          />
        </FormField>
        <FormField
          label="Late fee (€)"
          htmlFor={blueprintStageControlId(index, 'lateFee')}
          description="Needed on a night when entry ends before doors close."
        >
          <Input
            id={blueprintStageControlId(index, 'lateFee')}
            type="number"
            step="0.01"
            min={0}
            value={lateFee}
            onChange={(e) => setLateFee(e.target.value)}
          />
        </FormField>
      </div>
      <div className="flex flex-wrap gap-4">
        <Switch label="Website" checked={websiteSale} onChange={(e) => setWebsite(e.target.checked)} />
        <Switch label="App" checked={appSale} onChange={(e) => setApp(e.target.checked)} />
        <Switch
          id="admin.blueprints.posSale"
          label="POS"
          checked={posSale}
          onChange={(e) => setPos(e.target.checked)}
        />
        <Switch label="Door" checked={door} onChange={(e) => setDoor(e.target.checked)} />
        <Switch
          id="admin.blueprints.linkSale"
          label="Link"
          tone="yellow"
          checked={linkSale}
          onChange={(e) => setLink(e.target.checked)}
        />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Preview {formatCurrency(Number.parseFloat(price) || 0)} +{' '}
          {formatCurrency(Number.parseFloat(fee) || 0)} fee
        </p>
        <Button
          type="button"
          size="sm"
          loading={saving}
          onClick={async () => {
            setSaving(true);
            setSaveError(null);
            try {
              await onSave({
                name,
                ticketPriceCents: eurosToCents(Number.parseFloat(price) || 0),
                feePriceCents: eurosToCents(Number.parseFloat(fee) || 0),
                maxAmount: maxAmount ? Number.parseInt(maxAmount, 10) : null,
                entryFrom: entryFrom || null,
                entryUntil: entryUntil || null,
                lateFeeCents: lateFee.trim() ? eurosToCents(Number.parseFloat(lateFee) || 0) : null,
                websiteSale,
                appSale,
                posSale,
                door,
                linkSale,
                isActive,
              });
            } catch (err) {
              setSaveError(graphqlErrorMessage(err, 'Could not save this stage.'));
            } finally {
              setSaving(false);
            }
          }}
        >
          Save stage
        </Button>
      </div>
      {saveError ? (
        <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>
      ) : null}
    </div>
  );
}
